import type { ReactNode } from 'react';
import { Button } from './Button';
import { Panel } from './Panel';
import { secondaryTextClassName } from './styles';

// UX-34 — première vraie utilisation de ce composant dans ce projet (confirmé
// absent de `packages/ui/src` avant ce ticket, comme demandé par son propre
// prompt : "vérifie qu'il est bien disponible avant de l'utiliser"). Avant ce
// ticket, chaque page inventait son propre bloc "état vide" inline
// (`Children.tsx`/`DataRequests.tsx`/`Reenroll.tsx`, toutes des variantes
// mineures d'un `Panel` + `<p>` centré) — formalisé ici en un primitif
// PARTAGÉ (icône/titre/corps/action optionnelle), même précédent que
// `KpiCard`(UX-24)/`Modal`(UX-22)/`Avatar`(UX-11B) : construit dès qu'un
// vrai besoin apparaît, pas par anticipation.
//
// Portée DÉLIBÉRÉMENT limitée à son SEUL consommateur réel à ce jour
// (`Home.tsx`, UX-34) — les autres empty states inline existants
// (`Children.tsx`/`DataRequests.tsx`/`Reenroll.tsx`) ne sont PAS
// rétrofités par ce ticket, hors de son périmètre explicite.
export interface EmptyStateProps {
  icon: string;
  title: ReactNode;
  /**
   * Niveau de titre rendu pour `title` (UX-74, même patron que `titleAs` sur
   * `Card`/`Panel` — voir Card.tsx/Panel.tsx). `EmptyState` rendait
   * systématiquement un `<h3>` propre (pas via `Panel`, qu'il consomme sans
   * `title`), sans tenir compte du niveau de titre réel de la page
   * englobante. Défaut `h2`, même raisonnement que `Panel` : la plupart des
   * `EmptyState` sont une section de premier niveau sous le `<h1>` de la
   * page.
   */
  titleAs?: 'h2' | 'h3' | 'h4';
  body: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  'data-testid'?: string;
}

export function EmptyState({
  icon,
  title,
  titleAs: TitleTag = 'h2',
  body,
  actionLabel,
  onAction,
  'data-testid': testId,
}: EmptyStateProps) {
  return (
    <Panel data-testid={testId ?? 'empty-state'} className="flex flex-col items-center gap-2 py-8 text-center">
      <span aria-hidden="true" className="text-3xl">
        {icon}
      </span>
      <TitleTag className="text-base font-semibold text-text">{title}</TitleTag>
      <p className={secondaryTextClassName}>{body}</p>
      {actionLabel && onAction ? (
        <Button className="mt-2" onClick={onAction} data-testid="empty-state-action">
          {actionLabel}
        </Button>
      ) : null}
    </Panel>
  );
}
